import { useMemo } from 'react';
import { CONFIG } from 'constants/index';
import { ResourceType } from '../../../components/MenuTicket/types';
import { checkEnabledFeature, getKeyFeaturesConfig } from 'utils/function';

interface PermissionType {
  name?: string;
  key: string;
  enabled: boolean;
  features?: PermissionType[];
}

interface ParamsType {
  knowledge: PermissionType;
  pcManagement: PermissionType;
  smallCardDirectLink: PermissionType;
  requestTypes: PermissionType;
  passwordExpire: PermissionType;
  delegation: PermissionType;
  homepageSearchKnowledge: PermissionType;
  manageMyTeam: PermissionType;
  quickLinks: PermissionType;
  listVirtualDesktop: PermissionType;
  listVirtualApp: PermissionType;
}

const useFeatureFlags = ({
  knowledge,
  pcManagement,
  // smallCardDirectLink,
  requestTypes,
  passwordExpire,
  delegation,
  homepageSearchKnowledge,
  quickLinks,
  manageMyTeam,
  listVirtualDesktop,
  listVirtualApp,
}: ParamsType) => {
  const getFeature = (object: {
    features?: PermissionType[] | undefined;
  }): PermissionType[] | [] => {
    return object?.features || [];
  };

  const isEnabled = (object: { enabled: boolean }): boolean => {
    return object?.enabled;
  };

  const quickLinksFeature = getFeature(quickLinks);
  // const smallCardDirectLinkFeature = getFeature(smallCardDirectLink);
  const requestTypesFeature = getFeature(requestTypes);
  const knowledgeFeature = getFeature(knowledge);
  const pcManagementFeature = getFeature(pcManagement);
  const manageMyTeamFeature = getFeature(manageMyTeam);

  const isShowListVirtualDesktop = checkEnabledFeature(listVirtualDesktop);
  const isShowListVirtualApp = checkEnabledFeature(listVirtualApp);

  const isQuickLinksEnabled = isEnabled(quickLinks);
  const isRequestTypesEnabled = isEnabled(requestTypes);
  // const isSmallCardDirectLinkEnabled = isEnabled(smallCardDirectLink);
  const isPcManagementEnabled = isEnabled(pcManagement);
  const isKnowledgeEnabled = isEnabled(knowledge);
  const isHomepageSearchKnowledgeEnbaled = isEnabled(homepageSearchKnowledge);

  const isShowHomepageSearchKnowledge =
    isHomepageSearchKnowledgeEnbaled &&
    checkEnabledFeature(homepageSearchKnowledge);

  const getAnnouncement = getKeyFeaturesConfig(
    knowledgeFeature,
    CONFIG.KNOWLEDGE.ANNOUNCEMENTS,
  );
  const isShowAnnouncement =
    isKnowledgeEnabled && checkEnabledFeature(getAnnouncement);

  const getOutageList = getKeyFeaturesConfig(
    knowledgeFeature,
    CONFIG.KNOWLEDGE.OUTAGE_LIST,
  );

  const isShowOutageList =
    isKnowledgeEnabled && checkEnabledFeature(getOutageList);

  const getMajorOutageList = getKeyFeaturesConfig(
    knowledgeFeature,
    CONFIG.KNOWLEDGE.MAJOR_OUTAGE_LIST,
  );

  const isShowMajorOutageList = 
    isKnowledgeEnabled && checkEnabledFeature(getMajorOutageList);

  const getOutageEyeBrowEnabled = getKeyFeaturesConfig(
    getMajorOutageList?.features,
    CONFIG.KNOWLEDGE.OUTAGE_EYEBROW,
  );
  const isCheckOutageEyebrow = 
    isKnowledgeEnabled && checkEnabledFeature(getOutageEyeBrowEnabled);

  const getNewsList = getKeyFeaturesConfig(
    knowledgeFeature,
    CONFIG.KNOWLEDGE.NEWS_LIST,
  );

  const isShowNewsList = isKnowledgeEnabled && checkEnabledFeature(getNewsList);

  const topKnowledgeArticles = getKeyFeaturesConfig(
    knowledgeFeature,
    CONFIG.KNOWLEDGE.TOP_KNOWLEDGE_ARTICLES,
  );
  const isShowTopKnowledgeArticles =
    isKnowledgeEnabled && checkEnabledFeature(topKnowledgeArticles);

  const viewCountTooltip = getKeyFeaturesConfig(
    knowledgeFeature,
    CONFIG.KNOWLEDGE.VIEW_COUNT_TOOL_TIP,
  );
  const isShowViewCountTooltipConfig =
    isKnowledgeEnabled && checkEnabledFeature(viewCountTooltip);

  const publishedDate = getKeyFeaturesConfig(
    knowledgeFeature,
    CONFIG.KNOWLEDGE.PUBLISHED_DATE,
  );
  const isShowPublishedDateConfig =
    isKnowledgeEnabled && checkEnabledFeature(publishedDate);

  const showingSummaryOnHomepage = getKeyFeaturesConfig(
      knowledgeFeature,
      CONFIG.KNOWLEDGE.SHOWING_SUMMARY_ON_HOMEPAGE,
    );
  const isShowingSummaryOnHomepageConfig =
      isKnowledgeEnabled && checkEnabledFeature(showingSummaryOnHomepage);

  const viewCountShortNumber = getKeyFeaturesConfig(
    knowledgeFeature,
    CONFIG.KNOWLEDGE.VIEW_COUNT_SHORT_NUMBER,
  );
  const isShowViewCountShortNumberConfig =
    isKnowledgeEnabled && checkEnabledFeature(viewCountShortNumber);

  const viewCountShortNumberSpacing = getKeyFeaturesConfig(
    viewCountShortNumber?.features,
    CONFIG.KNOWLEDGE.VIEW_COUNT_SHORT_NUMBER_SPACING,
  );
  const isShowViewCountShortNumberSpacingConfig =
    isShowViewCountShortNumberConfig && checkEnabledFeature(viewCountShortNumberSpacing);

  const getCheckOutageKnowledge = getKeyFeaturesConfig(
    knowledgeFeature,
    CONFIG.KNOWLEDGE.OUTAGES,
  );
  const isCheckOutageKnowledge =
    isKnowledgeEnabled && checkEnabledFeature(getCheckOutageKnowledge);

  const getMyTechnology = getKeyFeaturesConfig(
    pcManagementFeature,
    CONFIG.PC_MANAGEMENT.MY_TECHNOLOGY,
  );
  const isShowMyTechnology =
    isEnabled(pcManagement) && checkEnabledFeature(getMyTechnology);

  const getYourGear = getKeyFeaturesConfig(
    quickLinksFeature,
    CONFIG.QUICK_LINKS.YOUR_GEAR,
  );
  const isShowYourGear =
    isQuickLinksEnabled && checkEnabledFeature(getYourGear);

  const getYourSoftWare = getKeyFeaturesConfig(
    quickLinksFeature,
    CONFIG.QUICK_LINKS.YOUR_SOFTWARE,
  );
  const isShowYourSoftWare =
    isQuickLinksEnabled && checkEnabledFeature(getYourSoftWare);

  const getM365Services = getKeyFeaturesConfig(
    quickLinksFeature,
    CONFIG.QUICK_LINKS.M365_SERVICES,
  );

  const isShowM365Services =
    isQuickLinksEnabled && checkEnabledFeature(getM365Services);

  const getMsTeamsOwned = getKeyFeaturesConfig(
    quickLinksFeature,
    CONFIG.QUICK_LINKS.MS_TEAMS_OWNED,
  );

  const isShowMsTeamsOwned =
    isQuickLinksEnabled && checkEnabledFeature(getMsTeamsOwned);

  const getCardTracker = getKeyFeaturesConfig(
    pcManagementFeature,
    CONFIG.PC_MANAGEMENT.CARD_TRACKER,
  );
  const isShowCardTracker =
    isPcManagementEnabled && checkEnabledFeature(getCardTracker);

  const getEnabledApprovals = getKeyFeaturesConfig(
    requestTypesFeature,
    ResourceType.Approvals,
  );
  const isShowEnabledApprovals =
    isRequestTypesEnabled && checkEnabledFeature(getEnabledApprovals);

  const getEnabledIncidents = getKeyFeaturesConfig(
    requestTypesFeature,
    ResourceType.Incidents,
  );
  const isShowEnabledIncidents =
    isRequestTypesEnabled && checkEnabledFeature(getEnabledIncidents);

  const getEnabledRequests = getKeyFeaturesConfig(
    requestTypesFeature,
    ResourceType.Requests,
  );
  const isShowEnabledRequests =
    isRequestTypesEnabled && checkEnabledFeature(getEnabledRequests);

  const getShowPrimaryLabel = getKeyFeaturesConfig(
    getFeature(getMyTechnology),
    CONFIG.PC_MANAGEMENT.SHOW_PRIMARY_LABEL,
  );
  const isShowPrimaryLabel =
    isPcManagementEnabled && checkEnabledFeature(getShowPrimaryLabel);

  const getShowSerialNumber = getKeyFeaturesConfig(
    getFeature(getMyTechnology),
    CONFIG.PC_MANAGEMENT.SHOW_SERIAL_NUMBER,
  );
  const isShowSerialNumber =
    isPcManagementEnabled && checkEnabledFeature(getShowSerialNumber);

  const getShowLastScanned = getKeyFeaturesConfig(
    getFeature(getMyTechnology),
    CONFIG.PC_MANAGEMENT.SHOW_LAST_SCANNED,
  );
  const isShowLastScanned =
    isPcManagementEnabled && checkEnabledFeature(getShowLastScanned);

  const getShowAssigned = getKeyFeaturesConfig(
    getFeature(getMyTechnology),
    CONFIG.PC_MANAGEMENT.SHOW_ASSIGNED,
  );
  const isShowAssigned =
    isPcManagementEnabled && checkEnabledFeature(getShowAssigned);

  const isShowPasswordExpireCard = checkEnabledFeature(passwordExpire);

  const isDelegationEnabled =
    isEnabled(delegation) && checkEnabledFeature(delegation);

  const getBtnResolve = getKeyFeaturesConfig(
    getEnabledIncidents?.features,
    CONFIG.REQUEST_TYPE.BTN_RESOLVE,
  );
  const isCheckBtnResolve =
    isRequestTypesEnabled &&
    checkEnabledFeature(getEnabledIncidents) &&
    checkEnabledFeature(getBtnResolve);
  
  const getBtnReopen = getKeyFeaturesConfig(
    getEnabledIncidents?.features,
    CONFIG.REQUEST_TYPE.BTN_REOPEN,
  );
  const isCheckBtnReopen =
    isRequestTypesEnabled &&
    checkEnabledFeature(getEnabledIncidents) &&
    checkEnabledFeature(getBtnReopen);

  const getBtnApprove = getKeyFeaturesConfig(
    getEnabledApprovals?.features,
    CONFIG.REQUEST_TYPE.BTN_APPROVE,
  );
  const isCheckBtnApprove =
    isRequestTypesEnabled &&
    checkEnabledFeature(getEnabledApprovals) &&
    checkEnabledFeature(getBtnApprove);

  const getBtnReject = getKeyFeaturesConfig(
    getEnabledApprovals?.features,
    CONFIG.REQUEST_TYPE.BTN_REJECT,
  );
  const isCheckBtnReject =
    isRequestTypesEnabled &&
    checkEnabledFeature(getEnabledApprovals) &&
    checkEnabledFeature(getBtnReject);

  const getRequestsUpload = getKeyFeaturesConfig(
    getEnabledRequests?.features,
    CONFIG.REQUEST_TYPE.REQUESTS_UPLOAD,
  );
  const isCheckRequestsUpload =
    isRequestTypesEnabled &&
    checkEnabledFeature(getEnabledIncidents) &&
    checkEnabledFeature(getRequestsUpload);

  const getRequestsComments = getKeyFeaturesConfig(
    getEnabledRequests?.features,
    CONFIG.REQUEST_TYPE.REQUESTS_COMMENTS,
  );
  const isCheckRequestsComments =
    isRequestTypesEnabled &&
    checkEnabledFeature(getEnabledIncidents) &&
    checkEnabledFeature(getRequestsComments);

  const getIncidentComment = getKeyFeaturesConfig(
    getEnabledIncidents?.features,
    CONFIG.REQUEST_TYPE.INCIDENT_COMMENT,
  );

  const isCheckIncidentComment = 
  isShowEnabledIncidents && 
  checkEnabledFeature(getIncidentComment);

  const getApprovalComment = getKeyFeaturesConfig(
    getEnabledApprovals?.features,
    CONFIG.REQUEST_TYPE.APPROVAL_COMMENT
  );

  const isCheckApprovalComment = 
  isShowEnabledApprovals && 
  checkEnabledFeature(getApprovalComment);

  // config for approver comment on dialog  
  const getApproverComment = getKeyFeaturesConfig(
    getEnabledApprovals?.features,
    CONFIG.REQUEST_TYPE.APPROVER_COMMENT
  );

  const isCheckApproverComment = 
  isShowEnabledApprovals &&
  checkEnabledFeature(getApproverComment);

  const getIncidentUpload = getKeyFeaturesConfig(
    getEnabledIncidents?.features,
    CONFIG.REQUEST_TYPE.INCIDENT_UPLOAD,
  );

  const isCheckIncidentUpload = 
  isShowEnabledIncidents &&
  checkEnabledFeature(getIncidentUpload);

  const getApprovalUpload = getKeyFeaturesConfig(
    getEnabledApprovals?.features,
    CONFIG.REQUEST_TYPE.INCIDENT_UPLOAD,
  );

  const isCheckApprovalUpload = 
  isShowEnabledApprovals &&
  checkEnabledFeature(getApprovalUpload);

  const getPCLifecycle = getKeyFeaturesConfig(
    pcManagementFeature,
    CONFIG.PC_MANAGEMENT.PC_LIFECYCLE,
  );

  const getPCOrder = getKeyFeaturesConfig(
    getPCLifecycle?.features,
    CONFIG.PC_MANAGEMENT.ORDER_NEW_DEVICE,
  );

  const getExceptionRequest = getKeyFeaturesConfig(
    getPCOrder?.features,
    CONFIG.PC_MANAGEMENT.EXCEPTION_REQUEST,
  );

  const isCheckExceptionRequest =
    pcManagement?.enabled &&
    checkEnabledFeature(getPCLifecycle) &&
    checkEnabledFeature(getPCOrder) &&
    checkEnabledFeature(getExceptionRequest);

  const getRequestVM = getKeyFeaturesConfig(
    getPCLifecycle?.features,
    CONFIG.PC_MANAGEMENT.REQUEST_VM,
  );
  const isCheckRequestVM =
    pcManagement?.enabled &&
    checkEnabledFeature(getPCLifecycle) &&
    checkEnabledFeature(getRequestVM);

  const getRequestVA = getKeyFeaturesConfig(
    getPCLifecycle?.features,
    CONFIG.PC_MANAGEMENT.REQUEST_VA,
  );
  const isCheckRequestVA =
    pcManagement?.enabled &&
    checkEnabledFeature(getPCLifecycle) &&
    checkEnabledFeature(getRequestVA);

  const getGroupDeviceManagers = getKeyFeaturesConfig(
    manageMyTeamFeature,
    CONFIG.USER_IN_GROUP.DEVICE_MANAGERS,
  );

  const getMyTeamCard = getKeyFeaturesConfig(
    manageMyTeamFeature,
    CONFIG.USER_IN_GROUP.MY_TEAM_CARD
  );
  const isCheckGroupDeviceManagers =
    manageMyTeam?.enabled && checkEnabledFeature(getGroupDeviceManagers);
  const isCheckMyTeamCard = manageMyTeam?.enabled && checkEnabledFeature(getMyTeamCard);

  return useMemo(
    () => ({
      isShowHomepageSearchKnowledge,
      isShowPrimaryLabel,
      isShowSerialNumber,
      isShowLastScanned,
      isShowAssigned,
      isShowPasswordExpireCard,
      isShowTopKnowledgeArticles,
      isCheckOutageKnowledge,
      isDelegationEnabled,
      isCheckExceptionRequest,

      //News And Knowledge Column
      isShowOutageList,
      isShowNewsList,
      isShowAnnouncement,
      isShowMajorOutageList,
      isCheckOutageEyebrow,

      // My Technology Features Column
      isShowMyTechnology,
      isShowYourGear,
      isShowYourSoftWare,
      isShowM365Services,
      isShowMsTeamsOwned,
      isQuickLinksEnabled,
      isCheckMyTeamCard,

      // My Support Features Column
      isShowCardTracker,
      isShowEnabledApprovals,
      isShowEnabledIncidents,
      isShowEnabledRequests,
      isCheckGroupDeviceManagers,

      //Ticket
      isCheckBtnApprove,
      isCheckBtnReject,
      isCheckBtnResolve,
      isCheckBtnReopen,
      isCheckRequestsUpload,
      isCheckRequestsComments,
      isCheckIncidentComment,
      isCheckApprovalComment,
      isCheckIncidentUpload,
      isCheckApprovalUpload,
      isCheckApproverComment,
      
      //Virtual Device
      isCheckRequestVM,
      isCheckRequestVA,
      isShowListVirtualDesktop,
      isShowListVirtualApp,

      //Knowledge Article
      isShowViewCountTooltipConfig,
      isShowPublishedDateConfig,
      isShowingSummaryOnHomepageConfig,
      isShowViewCountShortNumberConfig,
      isShowViewCountShortNumberSpacingConfig,
    }),
    [
      knowledge,
      pcManagement,
      // smallCardDirectLink,
      requestTypes,
      passwordExpire,
      homepageSearchKnowledge,
      manageMyTeam,
      quickLinks,
      listVirtualDesktop,
      listVirtualApp,
    ],
  );
};

export default useFeatureFlags;
